/**
 * Two of Mappings' tabs, from one form (ADR 0198):
 *
 * - **Email** (Counting until ADR 0132; ADR 0108, decision 5): what a sent
 *   email counts, and the email domains matching treats apart. A public
 *   domain never matches a client by domain (section 5.2.1), and email with
 *   only Axon's own domains on it is internal, so it isn't counted (section
 *   8.5; ADR 0122).
 * - **Vendors** (the owner, 2026-10-28: "vendors should probably be their own
 *   tab"): email with only vendors outside Axon is a vendor's, and so are
 *   calls to or from a vendor's phone number: neither is counted (ADRs 0132
 *   and 0194 decision 6). Anyone can add either from a Needs you block's
 *   Vendor… in their day; an owner keeps the lists here.
 *
 * One form, so a domain is still checked against every list, whichever tab
 * it's added on: a domain can be on only one.
 *
 * Each change saves as it's made, as the Autotask settings do (ADR 0082): a
 * number of minutes when its field is left or Enter is pressed, a domain or
 * phone number as it's added or removed. One save at a time, and a change made meanwhile is sent once
 * that's answered. A value that can't be saved is explained under its field
 * and isn't sent; a refused one goes back to what's saved, with the reason.
 * The API checks every value again, and audits each change.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useId, useRef, useState } from "react";
import { Link } from "react-router";

import { ApiError, retryUnlessRefused } from "../../api/client";
import type { AppSettings, SettingsChanges } from "../../api/types";
import { useSession } from "../../app/session-context";
import { useOneSaveAtATime } from "../../app/useOneSaveAtATime";
import { Panel } from "../../components/ui/Panel";
import { normalizeDomain } from "../../lib/emailDomains";
import { formatPhone, normalizePhone } from "../../lib/phones";

// Only the email a tech sends counts (ADR 0129): `email_received_minutes`
// stays in the settings, for an older release, but nothing here sets it.
type MinutesKey = "email_sent_minutes";
type DomainsKey = "public_email_domains" | "internal_email_domains" | "vendor_email_domains";
type NumbersKey = "vendor_phone_numbers";
type CountingKey = MinutesKey | DomainsKey | NumbersKey;
type Changes = Pick<SettingsChanges, CountingKey>;

/** The API's maximum: a day, the longest one Autotask time entry may be. */
const MAX_MINUTES = 24 * 60;

const MINUTES: readonly { key: MinutesKey; label: string; hint: string }[] = [
  {
    key: "email_sent_minutes",
    label: "Sent email",
    hint: "Each one counts this many minutes, ending when it was sent, where nothing else covers that time. 5 to start.",
  },
];

interface DomainField {
  key: DomainsKey;
  label: string;
  hint: string;
  empty: string;
  /** What a domain on this list is, said of one ("a vendor's domain"). */
  is: string;
  /** What a domain on another list can't be here ("public"). */
  be: string;
}

/** Why a domain on `other` can't go on `field`, as the API says it. */
function conflict(domain: string, field: DomainField, other: DomainField): string {
  return `${domain} is ${other.is}, so it can't be ${field.be}.`;
}

const PUBLIC: DomainField = {
  key: "public_email_domains",
  label: "Public email domains",
  hint: "Anyone can have an address at these, so an email matches a client by its exact address, never by one of these domains.",
  empty: "None: every domain can match a client.",
  is: "a public email domain",
  be: "public",
};

const AXONS_OWN: DomainField = {
  key: "internal_email_domains",
  label: "Axon's own domains",
  hint: "Email with only these domains on it is internal, so it isn't counted.",
  empty: "None: no email is internal.",
  is: "one of Axon's own domains",
  be: "one of Axon's own",
};

const VENDORS: DomainField = {
  key: "vendor_email_domains",
  label: "Vendors' email domains",
  hint: "Email with only these outside Axon is a vendor's, so it isn't counted. Anyone can add one from a Needs you block's Vendor… in their day; only an owner takes one off.",
  empty: "None yet: add one here, or from an email's block in the day view.",
  is: "a vendor's domain",
  be: "a vendor's",
};

const DOMAIN_FIELDS: readonly DomainField[] = [PUBLIC, AXONS_OWN, VENDORS];
/** The lists that say how email is matched; the vendors' list sits with their numbers. */
const MATCHING_FIELDS: readonly DomainField[] = [PUBLIC, AXONS_OWN];

const VENDOR_NUMBERS = {
  key: "vendor_phone_numbers",
  label: "Vendors' phone numbers",
  hint: "Calls to or from these numbers are a vendor's, so they aren't counted, for anyone. Anyone can add one from a Needs you call's Vendor… in their day; only an owner takes one off.",
  empty: "None yet: add one here.",
} as const;

const inputClass =
  "h-10 rounded-input border border-line bg-card px-3 text-text " +
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand";

const legendClass = "px-1 text-[12.5px] font-medium uppercase tracking-[0.08em] text-muted";

/** Why a number of minutes can't be saved, or null if it can. */
function minutesProblem(text: string): string | null {
  const value = text.trim();
  if (value === "") {
    return "enter a number of minutes.";
  }
  if (!/^-?\d+$/.test(value)) {
    return "use whole minutes.";
  }
  const minutes = Number(value);
  if (minutes < 0) {
    return "it can't be less than 0.";
  }
  if (minutes > MAX_MINUTES) {
    return "it can be at most 1,440 minutes (a day).";
  }
  return null;
}

function Status({ status }: { status: string | undefined }) {
  return (
    <p role="status" className="text-sm font-medium text-status-logged-fg empty:hidden">
      {status}
    </p>
  );
}

function NotSaved({ id, reason }: { id: string; reason: string | null | undefined }) {
  return reason ? (
    <p
      id={id}
      role="alert"
      className="rounded-row bg-status-needs-client-bg px-3 py-1 text-sm text-status-needs-client-fg"
    >
      Not saved: {reason}
    </p>
  ) : null;
}

function MinutesField({
  label,
  hint,
  shown,
  status,
  error,
  onSave,
}: {
  label: string;
  hint: string;
  /** What's being saved, while it saves; then what's saved. */
  shown: number;
  status: string | undefined;
  /** Why the server refused the last change. */
  error: string | undefined;
  onSave: (minutes: number) => void;
}) {
  const id = useId();
  // What's typed and not yet saved, if anything.
  const [draft, setDraft] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const reason = problem ?? error;

  function commit() {
    if (draft === null) {
      return;
    }
    const found = minutesProblem(draft);
    if (found !== null) {
      setProblem(found);
      return;
    }
    const minutes = Number(draft.trim());
    setDraft(null);
    if (minutes !== shown) {
      onSave(minutes);
    }
  }

  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="font-medium">
        {label}
      </label>
      <div className="flex items-center gap-2">
        <input
          id={id}
          type="number"
          inputMode="numeric"
          min={0}
          max={MAX_MINUTES}
          step={1}
          className={`${inputClass} w-28`}
          value={draft ?? String(shown)}
          aria-invalid={Boolean(reason)}
          aria-describedby={`${id}-unit ${id}-hint` + (reason ? ` ${id}-problem` : "")}
          onChange={(event) => {
            setDraft(event.target.value);
            setProblem(null);
          }}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              commit();
            } else if (event.key === "Escape") {
              setDraft(null);
              setProblem(null);
            }
          }}
        />
        <span id={`${id}-unit`} className="text-muted">
          minutes
        </span>
      </div>
      <p id={`${id}-hint`} className="text-sm text-muted">
        {hint}
      </p>
      <Status status={status} />
      <NotSaved id={`${id}-problem`} reason={reason} />
    </div>
  );
}

function DomainList({
  field,
  domains,
  others,
  status,
  error,
  onSave,
}: {
  field: DomainField;
  /** What's being saved, while it saves; then what's saved. */
  domains: readonly string[];
  /** The other lists, with their domains: none can be on two. */
  others: readonly { field: DomainField; domains: readonly string[] }[];
  status: string | undefined;
  /** Why the server refused the last change. */
  error: string | undefined;
  onSave: (domains: string[]) => void;
}) {
  const id = useId();
  const [text, setText] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const reason = problem ?? error;

  function add() {
    if (text.trim() === "") {
      setProblem("enter a domain, like example.com.");
      return;
    }
    const domain = normalizeDomain(text);
    if (domain === null) {
      setProblem(`“${text.trim()}” isn't an email domain. Enter one like example.com.`);
      return;
    }
    if (domains.includes(domain)) {
      setProblem(`${domain} is already here.`);
      return;
    }
    const taken = others.find((other) => other.domains.includes(domain));
    if (taken) {
      setProblem(conflict(domain, field, taken.field));
      return;
    }
    setText("");
    setProblem(null);
    onSave([...domains, domain]);
  }

  return (
    <div role="group" aria-labelledby={`${id}-label`} className="flex flex-col gap-2">
      <span id={`${id}-label`} className="font-medium">
        {field.label}
      </span>
      <p className="text-sm text-muted">{field.hint}</p>
      {domains.length === 0 ? (
        <p className="text-sm text-muted">{field.empty}</p>
      ) : (
        <ul aria-label={field.label} className="flex flex-col gap-1.5">
          {domains.map((domain) => (
            <li
              key={domain}
              className="flex items-center gap-2 rounded-row border border-line bg-card px-3 py-1.5 text-sm"
            >
              <span className="min-w-0 break-all font-mono">{domain}</span>
              <button
                type="button"
                aria-label={`Remove ${domain}`}
                className="ml-auto cursor-pointer rounded-pill px-2 text-muted hover:bg-brand-tint"
                onClick={() => {
                  setProblem(null);
                  onSave(domains.filter((d) => d !== domain));
                }}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <form
        className="flex flex-wrap gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          add();
        }}
      >
        <label htmlFor={`${id}-add`} className="sr-only">
          {`Add to ${field.label}`}
        </label>
        <input
          id={`${id}-add`}
          type="text"
          autoComplete="off"
          spellCheck={false}
          placeholder="example.com"
          className={`${inputClass} min-w-0 grow basis-40`}
          value={text}
          aria-invalid={Boolean(reason)}
          aria-describedby={reason ? `${id}-problem` : undefined}
          onChange={(event) => {
            setText(event.target.value);
            setProblem(null);
          }}
        />
        <button
          type="submit"
          className="h-10 cursor-pointer rounded-pill border border-line bg-card px-4 text-sm font-medium hover:bg-brand-tint"
        >
          Add
        </button>
      </form>
      <Status status={status} />
      <NotSaved id={`${id}-problem`} reason={reason} />
    </div>
  );
}

function NumberList({
  numbers,
  status,
  error,
  onSave,
}: {
  /** What's being saved, while it saves; then what's saved. In E.164. */
  numbers: readonly string[];
  status: string | undefined;
  /** Why the server refused the last change. */
  error: string | undefined;
  onSave: (numbers: string[]) => void;
}) {
  const id = useId();
  const [text, setText] = useState("");
  const [problem, setProblem] = useState<string | null>(null);
  const reason = problem ?? error;
  const field = VENDOR_NUMBERS;

  function add() {
    if (text.trim() === "") {
      setProblem("enter a phone number, like (555) 555-0100.");
      return;
    }
    const number = normalizePhone(text);
    if (number === null) {
      setProblem(`“${text.trim()}” isn't a phone number. Enter one like (555) 555-0100.`);
      return;
    }
    if (numbers.includes(number)) {
      setProblem(`${formatPhone(number)} is already here.`);
      return;
    }
    setText("");
    setProblem(null);
    onSave([...numbers, number]);
  }

  return (
    <div role="group" aria-labelledby={`${id}-label`} className="flex flex-col gap-2">
      <span id={`${id}-label`} className="font-medium">
        {field.label}
      </span>
      <p className="text-sm text-muted">{field.hint}</p>
      {numbers.length === 0 ? (
        <p className="text-sm text-muted">{field.empty}</p>
      ) : (
        <ul aria-label={field.label} className="flex flex-col gap-1.5">
          {numbers.map((number) => (
            <li
              key={number}
              className="flex items-center gap-2 rounded-row border border-line bg-card px-3 py-1.5 text-sm"
            >
              <span className="min-w-0 break-all font-mono">{formatPhone(number)}</span>
              <button
                type="button"
                aria-label={`Remove ${formatPhone(number)}`}
                className="ml-auto cursor-pointer rounded-pill px-2 text-muted hover:bg-brand-tint"
                onClick={() => {
                  setProblem(null);
                  onSave(numbers.filter((n) => n !== number));
                }}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <form
        className="flex flex-wrap gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          add();
        }}
      >
        <label htmlFor={`${id}-add`} className="sr-only">
          {`Add to ${field.label}`}
        </label>
        <input
          id={`${id}-add`}
          type="tel"
          autoComplete="off"
          placeholder="(555) 555-0100"
          className={`${inputClass} min-w-0 grow basis-40`}
          value={text}
          aria-invalid={Boolean(reason)}
          aria-describedby={reason ? `${id}-problem` : undefined}
          onChange={(event) => {
            setText(event.target.value);
            setProblem(null);
          }}
        />
        <button
          type="submit"
          className="h-10 cursor-pointer rounded-pill border border-line bg-card px-4 text-sm font-medium hover:bg-brand-tint"
        >
          Add
        </button>
      </form>
      <Status status={status} />
      <NotSaved id={`${id}-problem`} reason={reason} />
    </div>
  );
}

type Errors = Partial<Record<CountingKey, string>>;

/** Which tab the form is: Email's own settings, or the vendors'. */
type Part = "email" | "vendors";

function CountingForm({ settings, part }: { settings: AppSettings; part: Part }) {
  const { api } = useSession();
  const queryClient = useQueryClient();
  const [errors, setErrors] = useState<Errors>({});
  // The field "Saved." shows under: the last one changed, once confirmed.
  const [lastSaved, setLastSaved] = useState<CountingKey | null>(null);
  const lastTouched = useRef<CountingKey | null>(null);
  const save = useMutation({
    mutationFn: (changes: Changes) => api.updateSettings(changes),
    onSuccess: (result, changes) => {
      queryClient.setQueryData(["settings"], result);
      const keys = Object.keys(changes) as CountingKey[];
      setErrors((old) => Object.fromEntries(Object.entries(old).filter(([k]) => !(k in changes))));
      const touched = lastTouched.current;
      setLastSaved(touched !== null && touched in changes ? touched : (keys[0] ?? null));
    },
    onError: (error, changes) => {
      const keys = Object.keys(changes) as CountingKey[];
      const named = error instanceof ApiError ? error.fieldErrors.filter((e) => e.field in changes) : [];
      const failure = error instanceof Error ? error.message : "Something went wrong.";
      // Each refused field says why; a refusal naming none (or no answer)
      // shows under every field that was sent.
      const reasons: Errors = Object.fromEntries(
        named.length > 0 ? named.map((e) => [e.field, e.message]) : keys.map((k) => [k, failure]),
      );
      setErrors((old) => ({ ...old, ...reasons }));
      // A refusal may come from a change made elsewhere: show what's saved.
      if (error instanceof ApiError && error.status < 500) {
        return queryClient.invalidateQueries({ queryKey: ["settings"] });
      }
      return undefined;
    },
  });
  // One save at a time, for the whole section: changes made meanwhile combine.
  const { send, saving } = useOneSaveAtATime(save, (queued, value) => ({ ...queued, ...value }));

  // What's saved, from the cache: a save's answer is there at once, while the
  // query passes it on a moment later, and a field shouldn't flash its old
  // value between.
  const saved = queryClient.getQueryData<AppSettings>(["settings"]) ?? settings;

  function shown<K extends CountingKey>(key: K): AppSettings[K] {
    return saving?.value[key] ?? saved[key];
  }

  function change(changes: Changes) {
    const keys = Object.keys(changes) as CountingKey[];
    lastTouched.current = keys[0] ?? null;
    setLastSaved(null);
    setErrors((old) => Object.fromEntries(Object.entries(old).filter(([k]) => !(k in changes))));
    send(changes);
  }

  function statusOf(key: CountingKey): string | undefined {
    if (saving !== null && key in saving.value) {
      return "Saving…";
    }
    return key === lastSaved ? "Saved." : undefined;
  }

  /** A domain list, checked against the others: a domain can be on only one. */
  function domainList(field: DomainField) {
    return (
      <DomainList
        key={field.key}
        field={field}
        domains={shown(field.key)}
        others={DOMAIN_FIELDS.filter((other) => other !== field).map((other) => ({
          field: other,
          domains: shown(other.key),
        }))}
        status={statusOf(field.key)}
        error={errors[field.key]}
        onSave={(domains) => {
          change({ [field.key]: domains });
        }}
      />
    );
  }

  if (part === "vendors") {
    return (
      <div className="flex flex-col gap-5">
        <p className="text-muted">
          Vendors&apos; email and calls aren&apos;t counted toward anyone&apos;s time. Anyone can add
          one from a Needs you block&apos;s Vendor… in their day; only an owner takes one off, here.
          Each change saves as soon as you make it.
        </p>
        <fieldset className="rounded-card border border-line bg-panel-2 p-4">
          <legend className={legendClass}>Email domains and phone numbers</legend>
          <div className="grid gap-6 md:grid-cols-2">
            {domainList(VENDORS)}
            <NumberList
              numbers={shown(VENDOR_NUMBERS.key)}
              status={statusOf(VENDOR_NUMBERS.key)}
              error={errors[VENDOR_NUMBERS.key]}
              onSave={(numbers) => {
                change({ [VENDOR_NUMBERS.key]: numbers });
              }}
            />
          </div>
        </fieldset>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <p className="text-muted">
        How email counts toward a tech&apos;s time, and which domains it&apos;s matched by. Vendors&apos;
        domains are on{" "}
        <Link to="/mappings/vendors" className="font-semibold text-brand-ink hover:underline">
          Mappings → Vendors
        </Link>
        . Each change saves as soon as you make it.
      </p>
      <fieldset className="rounded-card border border-line bg-panel-2 p-4">
        <legend className={legendClass}>Email minutes</legend>
        <div className="grid gap-4 md:grid-cols-2">
          {MINUTES.map((field) => (
            <MinutesField
              key={field.key}
              label={field.label}
              hint={field.hint}
              shown={shown(field.key)}
              status={statusOf(field.key)}
              error={errors[field.key]}
              onSave={(minutes) => {
                change({ [field.key]: minutes });
              }}
            />
          ))}
        </div>
      </fieldset>
      <fieldset className="rounded-card border border-line bg-panel-2 p-4">
        <legend className={legendClass}>Email domains</legend>
        <div className="grid gap-6 md:grid-cols-2">
          {MATCHING_FIELDS.map((field) => domainList(field))}
        </div>
      </fieldset>
    </div>
  );
}

/** A tab's panel: its heading, and the form once the settings are in. */
function SettingsPanel({ part, heading, title }: { part: Part; heading: string; title: string }) {
  const { api } = useSession();
  const settings = useQuery({
    queryKey: ["settings"],
    queryFn: () => api.getSettings(),
    retry: retryUnlessRefused,
  });

  let body;
  // A failed reload keeps the form it already has: only a first load that
  // fails has nothing to show.
  if (settings.error && !settings.data) {
    body = (
      <p role="alert" className="text-muted">
        The settings could not be loaded. Reload the page to try again.
      </p>
    );
  } else if (!settings.data) {
    body = (
      <p role="status" className="text-muted">
        Loading…
      </p>
    );
  } else {
    body = <CountingForm settings={settings.data} part={part} />;
  }

  return (
    <Panel aria-labelledby={heading}>
      <h2 id={heading} className="text-lg font-semibold tracking-tight">
        {title}
      </h2>
      <div className="mt-3">{body}</div>
    </Panel>
  );
}

/** Mappings → Email. */
export function CountingSettings() {
  return <SettingsPanel part="email" heading="counting-heading" title="Email" />;
}

/** Mappings → Vendors (ADR 0198). */
export function VendorSettings() {
  return <SettingsPanel part="vendors" heading="vendors-heading" title="Vendors" />;
}
